import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TextField, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@utils/analytics';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

export const SearchBar = React.memo(({ value, onChange }: SearchBarProps) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);

  const analyticsTimer = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localValue);
    }, 300);

    // Track search with a longer debounce to avoid noisy partial-query events
    if (localValue.length >= 3) {
      analyticsTimer.current = setTimeout(() => {
        trackEvent('search', { search_term: localValue });
      }, 1000);
    }

    // Cleanup clears BOTH timers: it runs before the next effect invocation
    // (same restart-the-countdown semantics as before) AND on unmount, so a
    // pending analytics event can no longer fire after the component is gone.
    return () => {
      clearTimeout(timer);
      clearTimeout(analyticsTimer.current);
    };
  }, [localValue, onChange]);

  // Update local value when prop changes (e.g., clear button)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  return (
    <Box className="search-bar">
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('searchPlaceholder')}
        value={localValue}
        onChange={handleChange}
        inputProps={{
          // Placeholder text is not an accessible name — give the input one.
          'aria-label': t('a11y.searchLabel'),
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: false,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
          },
        }}
      />
    </Box>
  );
});
SearchBar.displayName = 'SearchBar';
